import { useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Check, Send, CheckCircle2, ShieldCheck, Building2, UserPlus, LogIn } from "lucide-react";
import { TopBar } from "@/components/TopBar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { AutoTextarea, TextField } from "@/components/tools/fields";
import { api, errorText } from "@/lib/api";

const PURPOSE = ["Complete SEND audits", "Save progress automatically", "Upload evidence", "Track actions", "Generate reports", "Export PDFs", "Access toolkits", "Access future dashboards", "Submit feedback"];
const ISOLATION = ["Separate database space", "Separate file storage", "Separate audit records", "Separate action plans", "Separate dashboards", "Separate user accounts"];
const MODULES = [
  { t: "SENDCO Health Check", d: "Interactive 1–5 scoring with overall score, domain scores, RAG assessment and suggested priorities." },
  { t: "SEND Provision Audit", d: "A comprehensive audit with a strengths report, areas for improvement and an improvement summary." },
  { t: "Parent Partnership Review", d: "A scored review giving a Parent Partnership Index and recommended actions." },
  { t: "EHCP Annual Review Tracker", d: "Track each review using pupil reference numbers only, with Due Soon, Due This Month and Overdue alerts." },
  { t: "SEND Development Planner", d: "Priorities, actions, leads, success criteria, review dates, RAG status and evidence, saved automatically." },
];
const PRIVACY = [
  "The SEND Accountability Project Ltd operates a privacy-by-design approach.",
  "Organisation workspaces are isolated from one another and accessible only to authorised users belonging to that organisation.",
  "Information entered by users is not routinely accessed as part of normal platform operation. Any support access must be authorised and logged.",
  "Schools remain responsible for ensuring that any information uploaded complies with applicable data protection legislation, organisational policies and local procedures.",
  "The platform is designed to support pseudonymised working practices wherever possible, for example 'Pupil A001' instead of a child's name.",
];
const INIT = { school: "", email: "", role: "", priorities: "", website: "" };

const InterestForm = () => {
  const [f, setF] = useState(INIT);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const set = (k) => (v) => setF((s) => ({ ...s, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!f.school.trim() || !f.email.trim() || !f.role.trim()) return setError("Please add your school name, contact email and role.");
    setStatus("sending");
    setError("");
    try {
      await api.post("/feedback", { pack: "resource-centre", school: f.school, email: f.email, role: f.role, build_next: f.priorities || "Interested in the Resource Centre", founding: "Yes", website: f.website });
      setStatus("sent");
    } catch (err) {
      setError(errorText(err));
      setStatus("idle");
    }
    return undefined;
  };
  if (status === "sent") {
    return (
      <div role="status" className="flex flex-col items-start gap-3 py-6" data-testid="rc-interest-success">
        <CheckCircle2 size={44} className="text-brand" aria-hidden />
        <h3 className="font-display text-3xl font-medium">Thank you. We'll be in touch.</h3>
      </div>
    );
  }
  return (
    <form onSubmit={submit} noValidate className="space-y-5" data-testid="rc-interest-form">
      <div className="grid gap-4 sm:grid-cols-2">
        <TextField id="rc-school" label="School name" value={f.school} onChange={set("school")} testid="rc-school" />
        <TextField id="rc-email" label="Contact email" type="email" autoComplete="email" value={f.email} onChange={set("email")} testid="rc-email" />
      </div>
      <TextField id="rc-role" label="Role" value={f.role} onChange={set("role")} testid="rc-role" />
      <AutoTextarea id="rc-priorities" label="Current SEND priorities" rows={4} value={f.priorities} onChange={set("priorities")} testid="rc-priorities" />
      <div className="hidden" aria-hidden="true"><label htmlFor="rc-website">Website</label><input id="rc-website" tabIndex={-1} value={f.website} onChange={(e) => set("website")(e.target.value)} /></div>
      <p className="text-sm text-ink-soft">Please do not include pupil-identifiable information.</p>
      {error && <p role="alert" className="font-bold" style={{ color: "var(--rag-red-fg)" }} data-testid="rc-error">{error}</p>}
      <button type="submit" disabled={status === "sending"} className="btn btn-brand" data-testid="rc-submit"><Send size={16} aria-hidden /> {status === "sending" ? "Sending…" : "Register interest"}</button>
    </form>
  );
};

const List = ({ items }) => (
  <ul className="grid gap-2 sm:grid-cols-2">
    {items.map((p) => <li key={p} className="flex gap-2.5 font-bold"><Check size={18} className="mt-0.5 shrink-0 text-brand" aria-hidden />{p}</li>)}
  </ul>
);

export default function ResourceCentre() {
  return (
    <>
      <TopBar minimal />
      <main id="main" tabIndex={-1} className="outline-none">
        <section className="pb-16 pt-[150px] lg:pt-[178px]" aria-labelledby="rc-title">
          <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
            <p className="eyebrow mb-5 flex items-center gap-2"><Lock size={14} aria-hidden /> Optional · Now open</p>
            <h1 id="rc-title" className="font-display text-[2.6rem] font-medium leading-[1] tracking-[-0.03em] sm:text-6xl">SENDCO <span className="italic text-brand">Resource Centre</span></h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-ink-soft">A secure, school-specific workspace for SENDCOs, Inclusion Leads, Headteachers and authorised staff. It's optional: the free toolkits stay open to everyone, with no registration, and save in your browser. Create an account when you want to save and return on any device.</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/workspace?mode=create" className="btn btn-brand text-base" data-testid="rc-cta-create"><Building2 size={18} aria-hidden /> Create your school workspace</Link>
              <Link to="/workspace?mode=join" className="btn btn-ghost text-base" data-testid="rc-cta-join"><UserPlus size={18} aria-hidden /> Join with an access code</Link>
              <Link to="/workspace?mode=signin" className="btn btn-ghost text-base" data-testid="rc-cta-signin"><LogIn size={18} aria-hidden /> Sign in</Link>
            </div>
            <div className="mt-10 rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8"><List items={PURPOSE} /></div>
          </div>
        </section>
        <section className="border-y border-line bg-surface py-16" aria-labelledby="rc-sec">
          <div className="mx-auto grid max-w-[1100px] gap-10 px-5 sm:px-8 lg:grid-cols-2">
            <Reveal>
              <h2 id="rc-sec" className="flex items-center gap-3 font-display text-3xl font-medium"><ShieldCheck aria-hidden /> Your own isolated workspace</h2>
              <p className="mt-3 text-ink-soft">Each school receives its own space. Users from one school cannot see other schools, their metrics, documents or audits.</p>
              <div className="mt-5"><List items={ISOLATION} /></div>
              <p className="mt-6 text-ink-soft">A SENDCO or Headteacher creates the workspace and receives an organisation access code, for example <code className="rounded bg-paper px-1.5 py-0.5 font-bold">GREENWOOD2027-4F2A</code>. Staff and governors join and sign in with their school email, a personal password and that code. Administrators can add optional two-step verification.</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-3xl font-medium">Modules in your workspace</h2>
              <ol className="mt-5 space-y-3">
                {MODULES.map((m, i) => <li key={m.t} className="rounded-2xl border-2 border-line bg-paper p-4"><p className="font-bold"><span className="font-display italic text-brand">{i + 1}.</span> {m.t}</p><p className="mt-1 text-sm text-ink-soft">{m.d}</p></li>)}
              </ol>
            </Reveal>
          </div>
        </section>
        <section className="py-16" aria-labelledby="rc-interest">
          <div className="mx-auto grid max-w-[1100px] gap-10 px-5 sm:px-8 lg:grid-cols-2">
            <Reveal>
              <h2 className="font-display text-3xl font-medium">Privacy by design</h2>
              {PRIVACY.map((p) => <p key={p} className="mt-3 text-ink-soft">{p}</p>)}
            </Reveal>
            <Reveal delay={0.1} className="rounded-[2rem] border-2 border-line bg-surface p-6 sm:p-8">
              <h2 id="rc-interest" className="font-display text-3xl font-medium">50 funded school places</h2>
              <p className="mb-5 mt-2 text-ink-soft">Interested in early access through the Founding Schools Programme? Tell us about your school.</p>
              <InterestForm />
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
